
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserBoard from 'Models/Security/Acl/UserBoard';
import VisitorsBoard from 'Models/Security/Acl/VisitorsBoard';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';
import moment from 'moment';


export interface IBoardEntityAttributes extends IModelAttributes {
	organisationname: string;
	duns: string;
	apacId: string;
	companynumber: string;
	abn: string;
	nzbn: string;
	companystatus: string;
	deregistered: Date;
	reasonderegistered: string;
	hasdeclarationofsolvency: string;
	deregistrationformcodes: string;

	directorss: Array<
		| Models.DirectorboardsEntity
		| Models.IDirectorboardsEntityAttributes
	>;

}

@entity('BoardEntity', 'Board')
export default class BoardEntity extends Model
	implements IBoardEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserBoard(),
		new VisitorsBoard(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<BoardEntity, string>()
	@CRUD({
		name: 'OrganisationName',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public organisationname: string;

	@observable
	@attribute<BoardEntity, string>()
	@CRUD({
		name: 'DUNS',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public duns: string;

	@observable
	@attribute<BoardEntity, string>()
	@CRUD({
		name: 'ApacId',
		displayType: 'textfield',
		order: 25,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public apacId: string;

	@observable
	@attribute<BoardEntity, string>()
	@CRUD({
		name: 'CompanyNumber',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public companynumber: string;

	@observable
	@attribute<BoardEntity, string>()
	@CRUD({
		name: 'ABN',
		displayType: 'textfield',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public abn: string;

	@observable
	@attribute<BoardEntity, string>()
	@CRUD({
		name: 'NZBN',
		displayType: 'textfield',
		order: 50,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public nzbn: string;

	@observable
	@attribute<BoardEntity, string>()
	@CRUD({
		name: 'CompanyStatus',
		displayType: 'textfield',
		order: 60,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public companystatus: string;
	

	
	@observable
	@attribute<BoardEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	@CRUD({
		name: 'Deregistered',
		displayType: 'datepicker',
		order: 70,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: attr => AttrUtils.standardiseDate(attr, { serialiseAs: 'date' }),
	})
	public deregistered: Date;

	@observable
	@attribute<BoardEntity, string>()
	@CRUD({
		name: 'ReasonDeregistered',
		displayType: 'textfield',
		order: 100,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public reasonderegistered: string;

	@observable
	@attribute<BoardEntity, string>()
	@CRUD({
		name: 'HasDeclarationOfSolvency',
		displayType: 'textfield',
		order: 110,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public hasdeclarationofsolvency: string;

	@observable
	@attribute<BoardEntity, string>()
	@CRUD({
		name: 'DeregistrationFormCodes',
		displayType: 'textfield',
		order: 120,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public deregistrationformcodes: string;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Directorss',
		displayType: 'reference-multicombobox',
		order: 130,
		referenceTypeFunc: () => Models.DirectorboardsEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'directorss',
			oppositeEntity: () => Models.DirectorboardsEntity,
		}),
		
	})
	public directorss: Models.DirectorboardsEntity[] = [];


	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IBoardEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IBoardEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.organisationname !== undefined) {
				this.organisationname = attributes.organisationname;
			}
			if (attributes.duns !== undefined) {
				this.duns = attributes.duns;
			}
			if (attributes.apacId !== undefined) {
				this.apacId = attributes.apacId;
			}
			if (attributes.companynumber !== undefined) {
				this.companynumber = attributes.companynumber;
			}
			if (attributes.abn !== undefined) {
				this.abn = attributes.abn;
			}
			if (attributes.nzbn !== undefined) {
				this.nzbn = attributes.nzbn;
			}
			if (attributes.companystatus !== undefined) {
				this.companystatus = attributes.companystatus;
			}
			if (attributes.deregistered !== undefined) {
				if (attributes.deregistered === null) {
					this.deregistered = attributes.deregistered;
				} else {
					this.deregistered = moment(attributes.deregistered).toDate();
				}
			}
			if (attributes.reasonderegistered !== undefined) {
				this.reasonderegistered = attributes.reasonderegistered;
			}
			if (attributes.hasdeclarationofsolvency !== undefined) {
				this.hasdeclarationofsolvency = attributes.hasdeclarationofsolvency;
			}
			if (attributes.deregistrationformcodes !== undefined) {
				this.deregistrationformcodes = attributes.deregistrationformcodes;
			}
			if (attributes.directorss !== undefined && Array.isArray(attributes.directorss)) {
				for (const model of attributes.directorss) {
					if (model instanceof Models.DirectorboardsEntity) {
						this.directorss.push(model);
					} else {
						this.directorss.push(new Models.DirectorboardsEntity(model));
					}
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		directorss {
			${Models.DirectorboardsEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			directorss: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(BoardEntity.prototype, 'created');
CRUD(modifiedAttr)(BoardEntity.prototype, 'modified');
